import { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { apiErrorMessage } from "../utils/apiError";
import {
 HiOutlinePlus,
 HiOutlinePencil,
 HiOutlineTrash,
 HiOutlineSearch,
 HiOutlineX,
 HiOutlineChevronLeft,
 HiOutlineMap,
 HiOutlineExclamation,
 HiOutlineUpload,
 HiOutlineDownload,
 HiOutlineDocumentText,
} from "react-icons/hi";
import {
 fetchMasterData,
 fetchStateById,
 createState,
 updateState,
 deleteState,
 bulkAction,
 bulkUpload,
} from "../services/masterDataApi";
import Toast from "../components/Toast";
import Pagination from "../components/Pagination";
import { useDebounce } from "../hooks/useDebounce";
import { useUserRole } from "../hooks/useUserRole";

function States() {
 const { canManageStates } = useUserRole();
 const [data, setData] = useState([]);
 const [loading, setLoading] = useState(true);
 /* ── views: "list" | "form" — backed by the URL so a refresh in edit mode
    reopens the same state instead of dropping back to the list ── */
 const [searchParams, setSearchParams] = useSearchParams();
 const view = searchParams.get("view") || "list";
 const urlEditId = searchParams.get("edit");
 const setView = useCallback((v, params = {}) => {
 if (v === "list") {
 setSearchParams({});
 } else if (v === "form" && params.edit) {
 setSearchParams({ view: "form", edit: params.edit });
 } else {
 setSearchParams({ view: v });
 }
 }, [setSearchParams]);
 const [editing, setEditing] = useState(null);
 const [formName, setFormName] = useState("");
 const [search, setSearch] = useState("");
 const debouncedSearch = useDebounce(search, 400);
 const [selectedIds, setSelectedIds] = useState([]);
 const [showBulkConfirm, setShowBulkConfirm] = useState(false);
 const [bulkLoading, setBulkLoading] = useState(false);
 const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
 const [toast, setToast] = useState(null);
 const selectAllRef = useRef(null);
 const fileInputRef = useRef(null);
 const [showUploadModal, setShowUploadModal] = useState(false);
 const [uploadFile, setUploadFile] = useState(null);
 const [uploadLoading, setUploadLoading] = useState(false);
 const [uploadResult, setUploadResult] = useState(null);
 const [isDragging, setIsDragging] = useState(false);

 // Pagination state
 const [currentPage, setCurrentPage] = useState(1);
 const [totalCount, setTotalCount] = useState(0);
 const [totalPages, setTotalPages] = useState(1);
 const [pageSize, setPageSize] = useState(20);

 // Update indeterminate state of select-all checkbox
 useEffect(() => {
 if (selectAllRef.current) {
 const pageIds = data.map((item) => item.id);
 const selectedInView = selectedIds.filter((id) => pageIds.includes(id));
 selectAllRef.current.indeterminate =
 selectedInView.length > 0 && selectedInView.length < pageIds.length;
 }
 }, [selectedIds, data]);

 const loadData = useCallback(async (page = currentPage, searchQuery = debouncedSearch) => {
 setLoading(true);
 try {
 const result = await fetchMasterData("states", {
 page,
 page_size: pageSize,
 search: searchQuery,
 });
 setData(result.data);
 setTotalCount(result.total_count);
 setTotalPages(result.total_pages);
 setCurrentPage(result.current_page);
 } catch (err) {
 console.error("Failed to load states", err);
 } finally {
 setLoading(false);
 }
 }, [currentPage, debouncedSearch, pageSize]);

 // Re-fetch when debounced search or page size changes
 useEffect(() => {
 setCurrentPage(1);
 loadData(1, debouncedSearch);
 }, [debouncedSearch, pageSize]);

 const handlePageSizeChange = (newSize) => {
 setPageSize(newSize);
 setCurrentPage(1);
 };

 const openCreate = () => {
 setEditing(null);
 setFormName("");
 setView("form");
 };

 const openEdit = (item) => {
 // A direct click already has the full item — mark the URL-restore effect
 // as satisfied so it doesn't also redundantly re-fetch this same item.
 hasRestoredFromUrl.current = true;
 setEditing(item);
 setFormName(item.name);
 setView("form", { edit: item.id });
 };

 // Restore edit view from URL on refresh — re-fetch the single state (its
 // GET returns { success, data: { id, name } }) and feed the unwrapped item
 // through the normal openEdit path.
 const hasRestoredFromUrl = useRef(false);
 useEffect(() => {
 if (hasRestoredFromUrl.current) return;
 if (view === "form" && urlEditId) {
 hasRestoredFromUrl.current = true;
 fetchStateById(urlEditId)
 .then((res) => openEdit(res.data))
 .catch(() => setView("list"));
 } else if (view === "form" && !urlEditId) {
 hasRestoredFromUrl.current = true;
 openCreate();
 }
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [view, urlEditId]);

 const handleSubmit = async (e) => {
 e.preventDefault();
 const trimmedName = formName.trim();
 if (!/\p{L}/u.test(trimmedName)) {
 setToast({ message: "Name must contain at least one letter (not just numbers, spaces, or symbols)", type: "error" });
 return;
 }
 try {
 let res;
 if (editing) {
 res = await updateState(editing.id, trimmedName);
 setView("list");
 } else {
 res = await createState(trimmedName);
 setView("list");
 }
 setToast({ message: res.message || (editing ? "State updated successfully." : "State created successfully."), type: "success" });
 loadData(currentPage, debouncedSearch);
 } catch (err) {
 console.error("Save failed", err);
 setToast({ message: apiErrorMessage(err, "Operation failed. Please try again."), type: "error" });
 }
 };

 const handleDelete = async (id) => {
 try {
  const res = await deleteState(id);
  setShowDeleteConfirm(null);
  setSelectedIds((prev) => prev.filter((sid) => sid !== id));
  if (view !== "list") setView("list");
  setToast({ message: res.message || "State deleted successfully.", type: "success" });
  loadData(currentPage, debouncedSearch);
 } catch (err) {
  console.error("Delete failed", err);
  setShowDeleteConfirm(null);
  const msg = err.response?.data?.detail || "Failed to delete state. Please try again.";
  setToast({ message: msg, type: "error" });
 }
 };

 /* Bulk Selection Handlers */
 const handleSelectRow = (id) => {
 setSelectedIds((prev) =>
 prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
 );
 };

 const handleSelectAll = () => {
 const pageIds = data.map((item) => item.id);
 const allSelected = pageIds.every((id) => selectedIds.includes(id));
 if (allSelected) {
 setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
 } else {
 setSelectedIds((prev) => [...new Set([...prev, ...pageIds])]);
 }
 };

 const clearSelection = () => setSelectedIds([]);

 const handleBulkDelete = async () => {
 if (bulkLoading) return;
 setBulkLoading(true);
 try {
 const res = await bulkAction("states", "delete", selectedIds);
 setShowBulkConfirm(false);
 setSelectedIds([]);
 const message = res.mapped_ids?.length
 ? `${res.message} ${res.mapped_message}`
 : (res.message || `Successfully deleted ${res.processed} state(s).`);
 setToast({ message, type: res.success === false ? "error" : "success" });
 loadData(currentPage, debouncedSearch);
 } catch (err) {
 console.error("Bulk delete failed", err);
 const msg =
 err.response?.data?.detail || "Bulk delete failed. Please try again.";
 setToast({ message: msg, type: "error" });
 setShowBulkConfirm(false);
 } finally {
 setBulkLoading(false);
 }
 };

 /* Bulk Upload Handlers */
 const openUploadModal = () => {
 setUploadFile(null);
 setUploadResult(null);
 setShowUploadModal(true);
 };

 const handleFileChange = (e) => {
 const f = e.target.files[0];
 if (f && !f.name.endsWith(".xlsx")) {
 setToast({ message: "Only .xlsx files are accepted", type: "error" });
 return;
 }
 setUploadFile(f || null);
 setUploadResult(null);
 };

 const handleDragOver = (e) => {
 e.preventDefault();
 e.stopPropagation();
 setIsDragging(true);
 };

 const handleDragLeave = (e) => {
 e.preventDefault();
 e.stopPropagation();
 setIsDragging(false);
 };

 const handleDrop = (e) => {
 e.preventDefault();
 e.stopPropagation();
 setIsDragging(false);
 const f = e.dataTransfer.files[0];
 if (f && !f.name.endsWith(".xlsx")) {
 setToast({ message: "Only .xlsx files are accepted", type: "error" });
 return;
 }
 setUploadFile(f || null);
 setUploadResult(null);
 };

 const handleUpload = async () => {
 if (!uploadFile || uploadLoading) return;
 setUploadLoading(true);
 setUploadResult(null);
 try {
 const res = await bulkUpload("states", uploadFile);
 setUploadResult(res);
 if (res.success) {
 setToast({ message: res.message || `Inserted ${res.inserted} state(s)`, type: "success" });
 loadData(currentPage, debouncedSearch);
 }
 } catch (err) {
 const detail = err.response?.data?.detail || "Upload failed";
 setUploadResult({ success: false, errors: [], message: detail });
 setToast({ message: detail, type: "error" });
 } finally {
 setUploadLoading(false);
 }
 };

 const downloadTemplate = () => {
 const { downloadXlsxTemplate } = require("../services/xlsxTemplate");
 downloadXlsxTemplate("states_template.xlsx", ["name"], [["Rajasthan"], ["UP"], ["Bihar"]]);
 };

 const renderTableContent = () => {
 if (loading) {
 return (
 <div className="p-8 space-y-4">
 {[1, 2, 3, 4].map((i) => (
 <div key={i} className="flex items-center gap-4 animate-pulse">
 <div className="w-5 h-5 bg-[#e5e7eb] rounded" />
 <div className="w-8 h-4 bg-[#e5e7eb] rounded" />
 <div className="flex-1 h-4 bg-[#e5e7eb] rounded" />
 <div className="w-24 h-4 bg-[#e5e7eb] rounded" />
 </div>
 ))}
 </div>
 );
 }
 if (data.length === 0) {
 return (
 <div className="text-center py-16 px-5">
 <div className="w-16 h-16 bg-[#f3f4f6] rounded-lg flex items-center justify-center mx-auto mb-4">
 <HiOutlineMap className="text-2xl text-[#8b949e]" />
 </div>
 <p className="m-0 text-sm font-medium text-[#57606a]">
 {search ? "No states match your search." : "No states found."}
 </p>
 <p className="m-0 text-xs text-[#8b949e] mt-1">
 {search ? "Try a different search term." : "Create your first state to get started."}
 </p>
 </div>
 );
 }

 const pageIds = data.map((item) => item.id);
 const allSelected =
 pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));

 return (
 <table className="w-full text-sm">
 <thead>
 <tr className="bg-[#f6f8fa] border-b border-[#e5e7eb]">
 {canManageStates && (
 <th className="w-12 text-center py-3.5 px-3">
 <input
 ref={selectAllRef}
 type="checkbox"
 className="w-4 h-4 rounded border-[#d0d5dd] text-[#0078d4] focus:ring-[#0078d4] cursor-pointer accent-[#0078d4]"
 checked={allSelected}
 onChange={handleSelectAll}
 title="Select all"
 />
 </th>
 )}
 <th className="text-left py-4 px-6 text-xs font-medium text-[#57606a] w-[60px]">#</th>
 <th className="text-left py-4 px-6 text-xs font-medium text-[#57606a]">Name</th>
 </tr>
 </thead>
 <tbody className="divide-y divide-[#e5e7eb]">
 {data.map((item, idx) => {
 const isSelected = selectedIds.includes(item.id);
 return (
 <tr
 key={item.id}
 className={`group transition-colors duration-150 cursor-pointer ${
 isSelected
 ? "bg-[#eff6ff] hover:bg-[#eff6ff]"
 : "hover:bg-gray-50"
 }`}
 onClick={() => openEdit(item)}
 >
 {canManageStates && (
 <td className="w-12 text-center py-4 px-3" onClick={(e) => e.stopPropagation()}>
 <input
 type="checkbox"
 className="w-[18px] h-[18px] rounded border-[#d0d5dd] text-[#0078d4] focus:ring-[#0078d4] cursor-pointer accent-[#0078d4]"
 checked={isSelected}
 onChange={() => handleSelectRow(item.id)}
 />
 </td>
 )}
 <td className="py-4 px-6 text-[#8b949e] font-medium text-xs">{(currentPage - 1) * pageSize + idx + 1}</td>
 <td className="py-4 px-6">
 <div className="flex items-center gap-3">
 <div className="w-8 h-8 bg-[#eff6ff] rounded-md flex items-center justify-center flex-shrink-0">
 <HiOutlineMap className="text-sm text-[#0078d4]" />
 </div>
 <span className="font-medium text-[#24292f]">{item.name}</span>
 </div>
 </td>
 </tr>
 );
 })}
 </tbody>
 </table>
 );
 };

 return (
 <div className="p-6">
 {toast && (
 <Toast
 message={toast.message}
 type={toast.type}
 onClose={() => setToast(null)}
 />
 )}

 {/* List View */}
 {view === "list" && (
 <div className="bg-white rounded-lg border border-[#e5e7eb] overflow-hidden">
 {/* Toolbar */}
 <div className="flex justify-between items-center px-6 py-4 border-b border-[#e5e7eb]">
 <div className="relative flex items-center flex-1 max-w-sm">
 <HiOutlineSearch className="absolute left-3.5 text-[#8b949e] text-sm" />
 <input
 type="text"
 className="w-full py-2.5 pr-8 pl-10 text-sm border border-[#d0d5dd] rounded-md text-[#24292f] bg-white transition-all duration-200 box-border placeholder:text-[#8b949e] focus:outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4]/10"
 placeholder="Search states…"
 value={search}
 onChange={(e) => setSearch(e.target.value)}
 />
 {search && (
 <button
 className="absolute right-2 bg-transparent border-none text-[#8b949e] cursor-pointer p-1 rounded-md hover:text-[#57606a] hover:bg-[#f3f4f6] transition-colors"
 onClick={() => setSearch("")}
 >
 <HiOutlineX className="text-sm" />
 </button>
 )}
 </div>
 <div className="flex items-center gap-2">
 {canManageStates && (
 <>
 <button
 className="inline-flex items-center gap-2 h-10 px-5 text-sm font-semibold rounded-md cursor-pointer transition-all duration-200 bg-white text-[#24292f] border border-[#d0d5dd] hover:bg-[#f3f4f6] hover:border-[#8b949e]"
 onClick={openUploadModal}
 >
 <HiOutlineUpload className="text-base" />
 Bulk Upload
 </button>
 <button
 className="inline-flex items-center gap-2 h-10 px-5 text-sm font-semibold border-none rounded-md cursor-pointer transition-all duration-200 bg-[#0078d4] text-white hover:bg-[#106ebe]"
 onClick={openCreate}
 >
 <HiOutlinePlus className="text-base" />
 Add State
 </button>
 </>
 )}
 </div>
 </div>

 {/* Bulk Action Toolbar */}
 {canManageStates && selectedIds.length > 0 && (
 <div className="flex items-center justify-between px-6 py-3 bg-[#eff6ff] border-b border-[#d0d5dd] animate-[fadeIn_0.2s_ease-out]">
 <div className="flex items-center gap-3">
 <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#0078d4] text-white text-xs font-bold">
 {selectedIds.length}
 </span>
 <span className="text-sm font-medium text-[#24292f]">
 {selectedIds.length === 1 ? "item selected" : "items selected"}
 </span>
 </div>
 <div className="flex items-center gap-2">
 <button
 className="inline-flex items-center gap-1.5 py-2 px-4 text-sm font-semibold rounded-lg cursor-pointer transition-all duration-200 bg-white text-[#57606a] border border-[#d0d5dd] hover:bg-[#f3f4f6]"
 onClick={clearSelection}
 >
 Clear
 </button>
 <button
 className="inline-flex items-center gap-1.5 py-2 px-4 text-sm font-semibold rounded-lg cursor-pointer transition-all duration-200 bg-red-600 text-white border-none hover:bg-red-700 shadow-sm"
 onClick={() => setShowBulkConfirm(true)}
 >
 <HiOutlineTrash className="text-sm" />
 Delete Selected
 </button>
 </div>
 </div>
 )}

 {/* Table */}
 {renderTableContent()}

 {/* Pagination */}
 {!loading && totalCount > 0 && (
 <Pagination
 currentPage={currentPage}
 totalPages={totalPages}
 totalCount={totalCount}
 pageSize={pageSize}
 onPageChange={(p) => loadData(p, debouncedSearch)}
 onPageSizeChange={handlePageSizeChange}
 entityName="states"
 />
 )}
 </div>
 )}

 {/* Create/Edit Form */}
 {view === "form" && (
 <div className="bg-white rounded-xl border border-[#e5e7eb] w-full">
 {/* Form Header */}
 <div className="flex justify-between items-center px-6 py-5 border-b border-[#e5e7eb]">
 <div className="flex items-center gap-3">
 <button
 onClick={() => setView("list")}
 className="w-8 h-8 flex items-center justify-center rounded-md text-[#57606a] hover:bg-[#f3f4f6] transition-all border-none bg-transparent cursor-pointer"
 title="Back"
 >
 <HiOutlineChevronLeft className="text-lg" />
 </button>
 <div>
 <h3 className="m-0 text-[15px] font-semibold text-[#24292f]">
 {!canManageStates ? "State Details" : editing ? "Edit State" : "Add New State"}
 </h3>
 <p className="m-0 mt-1 text-[13px] text-[#8b949e]">
 {!canManageStates ? "View state information" : editing ? "Update state information" : "Create a new state entry"}
 </p>
 </div>
 </div>
 {canManageStates && editing && (
 <button onClick={() => setShowDeleteConfirm(editing)} className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700">Delete</button>
 )}
 </div>

 <form onSubmit={handleSubmit}>
 <div className="p-6">
 <div>
 <label htmlFor="state-name" className="block mb-2 text-sm font-medium text-[#24292f]">
 State Name <span className="text-red-500">*</span>
 </label>
 <input
 id="state-name"
 className="w-full py-2.5 px-4 text-sm border border-[#d0d5dd] rounded-md text-[#24292f] bg-white transition-all duration-200 box-border focus:outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4]/10 disabled:bg-[#f6f8fa] disabled:text-[#57606a]"
 type="text"
 placeholder="Enter state name"
 value={formName}
 onChange={(e) => setFormName(e.target.value)}
 disabled={!canManageStates}
 required
 autoFocus
 />
 </div>
 </div>
 <div className="flex justify-end gap-3 px-6 py-4 border-t border-[#e5e7eb] bg-[#f6f8fa] rounded-b-lg">
 <button
 type="button"
 className="py-2.5 px-5 text-sm font-semibold rounded-md cursor-pointer transition-all duration-200 bg-white text-[#57606a] border border-[#d0d5dd] hover:bg-[#f3f4f6]"
 onClick={() => setView("list")}
 >
 {canManageStates ? "Cancel" : "Back"}
 </button>
 {canManageStates && (
 <button
 type="submit"
 className="py-2.5 px-5 text-sm font-semibold border-none rounded-md cursor-pointer transition-all duration-200 bg-[#0078d4] text-white hover:bg-[#106ebe]"
 >
 {editing ? "Save Changes" : "Create State"}
 </button>
 )}
 </div>
 </form>
 </div>
 )}

 {/* Single Delete Confirmation Modal */}
 {showDeleteConfirm && (
 <div
 className="fixed inset-0 bg-black/40 flex items-center justify-center z-[250] p-4"
 onClick={() => setShowDeleteConfirm(null)}
 >
 <div
 className="bg-white rounded-lg border border-[#e5e7eb] w-full max-w-sm shadow-[0_8px_24px_rgba(0,0,0,0.12)] animate-[fadeIn_0.2s_ease-out]"
 role="dialog"
 aria-modal="true"
 onClick={(e) => e.stopPropagation()}
 >
 <div className="p-6 text-center">
 <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mx-auto mb-4">
 <HiOutlineExclamation className="text-2xl text-red-600" />
 </div>
 <h3 className="m-0 text-lg font-semibold text-[#24292f]">Delete State?</h3>
 <p className="m-0 mt-2 text-sm text-[#57606a] leading-relaxed">
 Are you sure you want to delete <strong>{showDeleteConfirm.name}</strong>? This action cannot be undone.
 </p>
 </div>
 <div className="flex gap-3 px-6 py-4 border-t border-[#e5e7eb] bg-[#f6f8fa] rounded-b-lg">
 <button
 className="flex-1 py-2.5 px-5 text-sm font-semibold rounded-md cursor-pointer transition-all duration-200 bg-white text-[#57606a] border border-[#d0d5dd] hover:bg-[#f3f4f6]"
 onClick={() => setShowDeleteConfirm(null)}
 >
 Cancel
 </button>
 <button
 className="flex-1 py-2.5 px-5 text-sm font-semibold border-none rounded-md cursor-pointer transition-all duration-200 bg-red-600 text-white hover:bg-red-700 shadow-sm"
 onClick={() => handleDelete(showDeleteConfirm.id)}
 >
 Delete
 </button>
 </div>
 </div>
 </div>
 )}

 {/* Bulk Delete Confirmation Modal */}
 {showBulkConfirm && (
 <div
 className="fixed inset-0 bg-black/40 flex items-center justify-center z-[250] p-4"
 onClick={() => !bulkLoading && setShowBulkConfirm(false)}
 >
 <div
 className="bg-white rounded-lg border border-[#e5e7eb] w-full max-w-sm shadow-[0_8px_24px_rgba(0,0,0,0.12)] animate-[fadeIn_0.2s_ease-out]"
 role="dialog"
 aria-modal="true"
 onClick={(e) => e.stopPropagation()}
 onKeyDown={(e) => { if (e.key === 'Escape' && !bulkLoading) setShowBulkConfirm(false); }}
 >
 <div className="p-6 text-center">
 <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mx-auto mb-4">
 <HiOutlineExclamation className="text-2xl text-red-600" />
 </div>
 <h3 className="m-0 text-lg font-semibold text-[#24292f]">Confirm Bulk Delete</h3>
 <p className="m-0 mt-2 text-sm text-[#57606a] leading-relaxed">
 You are about to delete{" "}
 <span className="font-bold text-[#24292f]">{selectedIds.length}</span>{" "}
 state{selectedIds.length !== 1 ? "s" : ""}. This action cannot be undone.
 </p>
 </div>
 <div className="flex gap-3 px-6 py-4 border-t border-[#e5e7eb] bg-[#f6f8fa] rounded-b-lg">
 <button
 className="flex-1 py-2.5 px-5 text-sm font-semibold rounded-md cursor-pointer transition-all duration-200 bg-white text-[#57606a] border border-[#d0d5dd] hover:bg-[#f3f4f6] disabled:opacity-50 disabled:cursor-not-allowed"
 onClick={() => setShowBulkConfirm(false)}
 disabled={bulkLoading}
 >
 Cancel
 </button>
 <button
 className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 px-5 text-sm font-semibold border-none rounded-md cursor-pointer transition-all duration-200 bg-red-600 text-white hover:bg-red-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
 onClick={handleBulkDelete}
 disabled={bulkLoading}
 >
 {bulkLoading ? (
 <>
 <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
 <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
 <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
 </svg>
 Deleting…
 </>
 ) : (
 "Confirm Delete"
 )}
 </button>
 </div>
 </div>
 </div>
 )}
 {/* Bulk Upload Modal */}
 {showUploadModal && (
 <div
 className="fixed inset-0 bg-black/40 flex items-center justify-center z-[200] p-4"
 onClick={() => !uploadLoading && setShowUploadModal(false)}
 >
 <div
 className="bg-white rounded-lg border border-[#e5e7eb] w-full max-w-md shadow-[0_8px_24px_rgba(0,0,0,0.12)] animate-[fadeIn_0.2s_ease-out]"
 role="dialog"
 aria-modal="true"
 onClick={(e) => e.stopPropagation()}
 onKeyDown={(e) => { if (e.key === 'Escape' && !uploadLoading) setShowUploadModal(false); }}
 >
 {/* Header */}
 <div className="flex justify-between items-center px-6 py-4 border-b border-[#e5e7eb]">
 <div>
 <h3 className="m-0 text-base font-semibold text-[#24292f]">Bulk Upload States</h3>
 <p className="m-0 mt-0.5 text-xs text-[#8b949e]">Upload an Excel file (.xlsx) to import states</p>
 </div>
 <button
 className="w-8 h-8 flex items-center justify-center rounded-md text-[#8b949e] hover:text-[#57606a] hover:bg-[#f3f4f6] transition-all duration-200 border-none bg-transparent cursor-pointer"
 onClick={() => setShowUploadModal(false)}
 disabled={uploadLoading}
 >
 <HiOutlineX className="text-lg" />
 </button>
 </div>

 {/* Body */}
 <div className="p-6 space-y-5">
 {/* Template Download */}
 <div className="flex items-center gap-3 p-3 bg-[#eff6ff] rounded-md border border-[#d0d5dd]">
 <HiOutlineDocumentText className="text-lg text-[#0078d4] flex-shrink-0" />
 <div className="flex-1">
 <p className="m-0 text-sm font-medium text-[#24292f]">Need a template?</p>
 <p className="m-0 text-xs text-[#57606a] mt-0.5">Columns: name</p>
 </div>
 <button
 className="inline-flex items-center gap-1 py-1.5 px-3 text-xs font-semibold rounded-lg cursor-pointer transition-all duration-200 bg-white text-[#0078d4] border border-[#d0d5dd] hover:bg-[#f3f4f6]"
 onClick={downloadTemplate}
 >
 <HiOutlineDownload className="text-sm" />
 Download
 </button>
 </div>

 {/* File Input */}
 <div>
 <label className="block mb-2 text-sm font-medium text-[#24292f]">Excel File (.xlsx)</label>
 <div
 className={`relative border-2 border-dashed rounded-md p-6 text-center transition-colors cursor-pointer ${isDragging ? "border-[#0078d4] bg-[#eff6ff]" : "border-[#d0d5dd] hover:border-[#0078d4]"}`}
 onClick={() => fileInputRef.current?.click()}
 onDragOver={handleDragOver}
 onDragLeave={handleDragLeave}
 onDrop={handleDrop}
 >
 <input
 ref={fileInputRef}
 type="file"
 accept=".xlsx"
 className="hidden"
 onChange={handleFileChange}
 />
 <HiOutlineUpload className="text-2xl text-[#8b949e] mx-auto mb-2" />
 {uploadFile ? (
 <p className="m-0 text-sm font-medium text-[#24292f]">{uploadFile.name}</p>
 ) : (
 <p className="m-0 text-sm text-[#8b949e]">Click to select or drag a file</p>
 )}
 <p className="m-0 text-xs text-[#8b949e] mt-1">Max 5MB · Max 1000 rows</p>
 </div>
 </div>

 {/* Upload Result */}
 {uploadResult && (
 <div className={`p-4 rounded-xl border ${
 uploadResult.success
 ? "bg-[#f0fdf4] border-[#bbf7d0]"
 : "bg-red-50 border-red-200"
 }`}>
 <p className={`m-0 text-sm font-semibold ${
 uploadResult.success ? "text-[#16a34a]" : "text-red-700"
 }`}>
 {uploadResult.success
 ? ` Inserted ${uploadResult.inserted} record(s)`
 : uploadResult.message || "Validation failed"}
 </p>
 {uploadResult.errors && uploadResult.errors.length > 0 && (
 <div className="mt-3 max-h-40 overflow-y-auto space-y-1">
 {uploadResult.errors.map((err, i) => (
 <p key={i} className="m-0 text-xs text-red-600">
 Row {err.row}: {err.message}
 </p>
 ))}
 </div>
 )}
 </div>
 )}
 </div>

 {/* Footer */}
 <div className="flex justify-end gap-3 px-6 py-4 border-t border-[#e5e7eb] bg-[#f6f8fa] rounded-b-lg">
 <button
 className="py-2.5 px-5 text-sm font-semibold rounded-md cursor-pointer transition-all duration-200 bg-white text-[#57606a] border border-[#d0d5dd] hover:bg-[#f3f4f6] disabled:opacity-50"
 onClick={() => setShowUploadModal(false)}
 disabled={uploadLoading}
 >
 {uploadResult?.success ? "Close" : "Cancel"}
 </button>
 {!uploadResult?.success && (
 <button
 className="inline-flex items-center justify-center gap-2 py-2.5 px-5 text-sm font-semibold border-none rounded-md cursor-pointer transition-all duration-200 bg-[#0078d4] text-white hover:bg-[#106ebe] shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
 onClick={handleUpload}
 disabled={!uploadFile || uploadLoading}
 >
 {uploadLoading ? (
 <>
 <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
 <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
 <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
 </svg>
 Uploading…
 </>
 ) : (
 <>
 <HiOutlineUpload className="text-base" />
 Upload
 </>
 )}
 </button>
 )}
 </div>
 </div>
 </div>
 )}
 </div>
 );
}

export default States;
